import { useIntl, defineMessages } from 'react-intl'
import styled from 'styled-components'
import { Formik } from 'formik'

import format from 'date-fns/format'
import subDays from 'date-fns/subDays'
import subMonths from 'date-fns/subMonths'
import subYears from 'date-fns/subYears'

import { Row } from '@10xLabs/react-web/components/Row'
import { Column } from '@10xLabs/react-web/components/Column'
import { Select } from 'components/v2/Select'
import { MultiCompanyCheckboxes } from 'features/Companies/components/MultiCompanyCheckboxes'
import { SitesCheckboxes } from 'features/Sites/components/SitesCheckboxes'
import { DRAWER_STATUS } from 'features/SitesSales/components/DrawerStatus/constants'
import { drawerStatusMessages } from 'features/SitesSales/components/DrawerStatus/messages'
import { MultiCashierCheckboxes } from 'features/Users/components/MultiCashierCheckboxes'
import { isSuperAdmin } from 'features/Users/utils'
import { DATE_FORMAT } from 'utils/date'
import { useLogin } from 'features/Login/hooks'
import { GTUserSuggestion } from 'gql/types.generated'
import { ReactElement } from 'react'

export const TIMEFRAMES = {
  TODAY: 'today',
  YESTERDAY: 'yesterday',
  SEVEN_DAYS: 'seven_days',
  ONE_MONTH: 'one_month',
  THREE_MONTHS: 'three_months',
  ONE_YEAR: 'one_year'
}

export const TIMEFRAMES_LIST = [
  TIMEFRAMES.TODAY,
  TIMEFRAMES.YESTERDAY,
  TIMEFRAMES.SEVEN_DAYS,
  TIMEFRAMES.ONE_MONTH,
  TIMEFRAMES.THREE_MONTHS,
  TIMEFRAMES.ONE_YEAR
]

const DRAWER_STATUS_LIST = [DRAWER_STATUS.OPEN, DRAWER_STATUS.CLOSED]

export const getTimeframeDates = (timeframe: TSFixMe): TSFixMe => {
  let yesterday
  const today = new Date()

  switch (timeframe) {
    case TIMEFRAMES.TODAY:
      return {
        from: format(today, DATE_FORMAT),
        to: format(today, DATE_FORMAT)
      }
    case TIMEFRAMES.YESTERDAY:
      yesterday = subDays(today, 1)

      return {
        from: format(yesterday, DATE_FORMAT),
        to: format(yesterday, DATE_FORMAT)
      }
    case TIMEFRAMES.ONE_MONTH:
      return {
        from: format(subMonths(today, 1), DATE_FORMAT),
        to: format(today, DATE_FORMAT)
      }
    case TIMEFRAMES.THREE_MONTHS:
      return {
        from: format(subMonths(today, 3), DATE_FORMAT),
        to: format(today, DATE_FORMAT)
      }
    case TIMEFRAMES.ONE_YEAR:
      return {
        from: format(subYears(today, 1), DATE_FORMAT),
        to: format(today, DATE_FORMAT)
      }
    default:
      return {
        from: format(subDays(today, 6), DATE_FORMAT),
        to: format(today, DATE_FORMAT)
      }
  }
}

export const FiltersWrapper = styled.div`
  padding: 20px;
  background-color: #ffffff;
  border-bottom: 1px solid rgba(153, 153, 189, 0.3);
  height: 45px;
`

const timeframesMessages = defineMessages({
  [TIMEFRAMES.TODAY]: {
    id: 'timeframe.today',
    defaultMessage: 'Today'
  },
  [TIMEFRAMES.YESTERDAY]: {
    id: 'timeframe.yesterday',
    defaultMessage: 'Yesterday'
  },
  [TIMEFRAMES.SEVEN_DAYS]: {
    id: 'timeframe.sevenDays',
    defaultMessage: '7 days'
  },
  [TIMEFRAMES.ONE_MONTH]: {
    id: 'timeframe.oneMonth',
    defaultMessage: '1 month'
  },
  [TIMEFRAMES.THREE_MONTHS]: {
    id: 'timeframe.threeMonths',
    defaultMessage: '3 months'
  },
  [TIMEFRAMES.ONE_YEAR]: {
    id: 'timeframe.oneYear',
    defaultMessage: '1 year'
  }
})

const messages = defineMessages({
  allStatus: {
    id: 'drawers.filters.status.all',
    defaultMessage: 'All Status'
  }
})

export const Filters = ({ filters, showEmployees, showDrawerStatus, applyFilters }: TSFixMe): ReactElement => {
  const intl = useIntl()
  const login = useLogin()

  const initialValues = {
    companies: filters.companies || [],
    siteIds: filters.siteIds || [],
    cashiers: filters.cashiers || [],
    drawerStatus: filters.drawerStatus,
    timeframe: filters.timeframe
  }

  return (
    <Formik initialValues={initialValues} onSubmit={applyFilters} enableReinitialize>
      {(props): TSFixMe => {
        const { values, setFieldValue, setValues } = props

        return (
          <Row>
            {isSuperAdmin(login.accessToken.conditions) && (
              <Column>
                <MultiCompanyCheckboxes
                  values={values.companies}
                  onChange={(companies): void => {
                    setValues({
                      ...values,
                      companies,
                      siteIds: [],
                      cashiers: []
                    })

                    applyFilters({
                      ...values,
                      companies,
                      siteIds: []
                    })
                  }}
                />
              </Column>
            )}

            <Column>
              <SitesCheckboxes
                // @ts-expect-error TS(2559) FIXME Remove this comment to see the full error message
                id="sites"
                siteIds={values.siteIds}
                companyIds={values.companies.map(({ id }: { id: string }): string => id)}
                onChange={(siteIds: TSFixMe): TSFixMe => {
                  setValues({
                    ...values,
                    siteIds,
                    cashiers: []
                  })

                  applyFilters({
                    ...values,
                    siteIds
                  })
                }}
              />
            </Column>

            {showEmployees && (
              <Column>
                <MultiCashierCheckboxes
                  values={values.cashiers}
                  siteIds={values.siteIds}
                  onChange={(cashiers: GTUserSuggestion[]): void => {
                    setFieldValue('cashiers', cashiers)

                    applyFilters({
                      ...values,
                      cashiers
                    })
                  }}
                />
              </Column>
            )}

            <Column>
              <Select
                id="timeframe"
                value={values.timeframe}
                onChange={({ target: { value } }): TSFixMe => {
                  setFieldValue('timeframe', value)

                  applyFilters({
                    ...values,
                    timeframe: value
                  })
                }}
              >
                {TIMEFRAMES_LIST.map((timeframe): TSFixMe => {
                  return (
                    <option value={timeframe} key={timeframe}>
                      {intl.formatMessage(timeframesMessages[timeframe])}
                    </option>
                  )
                })}
              </Select>
            </Column>

            {showDrawerStatus && (
              <Column>
                <Select
                  id="drawerStatus"
                  value={values.drawerStatus || 0}
                  onChange={({ target: { value } }): TSFixMe => {
                    const drawerStatus = value === '0' ? '' : value
                    setFieldValue('drawerStatus', drawerStatus)

                    applyFilters({
                      ...values,
                      drawerStatus
                    })
                  }}
                >
                  <option value={0}>{intl.formatMessage(messages.allStatus)}</option>

                  {DRAWER_STATUS_LIST.map((status): TSFixMe => {
                    return (
                      <option value={status} key={status}>
                        {intl.formatMessage(drawerStatusMessages[status])}
                      </option>
                    )
                  })}
                </Select>
              </Column>
            )}
          </Row>
        )
      }}
    </Formik>
  )
}
